import React, { useState, useEffect } from 'react';
import { RefreshCw, Download, X } from 'lucide-react';

const UpdatePrompt = () => {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [showPrompt, setShowPrompt] = useState(false);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    if ('serviceWorker' in navigator) {
      // Écouter les mises à jour du service worker
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        // Une mise à jour est devenue active
        if (!updating) {
          window.location.reload();
        }
      });

      // Vérifier périodiquement les mises à jour
      const checkForUpdates = async () => {
        try {
          const registration = await navigator.serviceWorker.getRegistration();
          if (registration) {
            await registration.update();
            
            // Si un nouveau worker est en attente
            if (registration.waiting) {
              setUpdateAvailable(true);
              setShowPrompt(true);
            }
            
            // Écouter les nouveaux workers qui s'installent
            registration.addEventListener('updatefound', () => {
              const newWorker = registration.installing;
              if (newWorker) {
                newWorker.addEventListener('statechange', () => {
                  if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                    setUpdateAvailable(true);
                    setShowPrompt(true);
                  }
                });
              }
            });
          }
        } catch (error) {
          console.error('Erreur lors de la vérification des mises à jour:', error);
        }
      };

      // Vérifier au chargement
      checkForUpdates();

      // Vérifier périodiquement (toutes les 30 minutes)
      const interval = setInterval(checkForUpdates, 30 * 60 * 1000);

      return () => {
        clearInterval(interval);
      };
    }
  }, [updating]);

  const handleUpdate = async () => {
    if (!updateAvailable) return;

    setUpdating(true);
    
    try {
      const registration = await navigator.serviceWorker.getRegistration();
      if (registration && registration.waiting) {
        // Dire au service worker en attente de prendre le contrôle
        registration.waiting.postMessage({ type: 'SKIP_WAITING' });
      }
    } catch (error) {
      console.error('Erreur lors de la mise à jour:', error);
      setUpdating(false);
    }
  };

  const dismissPrompt = () => {
    setShowPrompt(false);
    // Ne pas reproposer pendant 1 heure
    setTimeout(() => {
      setShowPrompt(true);
    }, 60 * 60 * 1000);
  };

  if (!showPrompt || !updateAvailable) {
    return null;
  }

  return (
    <div className="fixed top-4 right-4 z-50 max-w-sm animate-in slide-in-from-right duration-500">
      <div className="bg-white rounded-2xl shadow-2xl border border-orange-200/50 overflow-hidden">
        <div className="bg-gradient-to-r from-orange-500 to-amber-500 px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div className="bg-white/20 rounded-full p-1">
                <Download className="h-4 w-4 text-white" />
              </div>
              <span className="text-white font-semibold text-sm">
                Mise à jour disponible
              </span>
            </div>
            
            <button
              onClick={dismissPrompt}
              className="text-white/80 hover:text-white transition-colors"
              aria-label="Fermer"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
        
        <div className="p-4">
          <p className="text-gray-600 text-sm mb-4">
            Une nouvelle version de Korean Lash Lift by Ali est disponible avec des améliorations et corrections.
          </p>
          
          <div className="flex space-x-2">
            <button
              onClick={handleUpdate}
              disabled={updating}
              className="flex-1 bg-gradient-to-r from-orange-500 to-amber-500 text-white px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-200 hover:shadow-lg disabled:opacity-50 flex items-center justify-center space-x-2"
            >
              {updating ? (
                <>
                  <RefreshCw className="h-4 w-4 animate-spin" />
                  <span>Mise à jour...</span>
                </>
              ) : (
                <>
                  <RefreshCw className="h-4 w-4" />
                  <span>Mettre à jour</span>
                </>
              )}
            </button>
            
            <button
              onClick={dismissPrompt}
              className="px-4 py-2 text-gray-500 hover:text-gray-700 text-sm font-medium transition-colors"
            >
              Plus tard
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UpdatePrompt;